import {
  GridFilterItem,
  GridFilterOperator,
  GridSingleSelectColDef,
  GridValidRowModel,
  getGridSingleSelectOperators,
  getGridStringOperators,
  getGridBooleanOperators,
  ValueOptions
} from '@mui/x-data-grid-pro';
import { InventoryAsset, SupportCasesItem, User, EntityVoiceDetailsRecord } from '../../../types';
import { WorkflowOrder } from '../../../Api/Orders/ordersApi';
import { ServiceRequest } from '../../../Api/ServiceRequest/serviceRequestApi';
import { GridPreferences, PrefKey, getCurrentPreferences, getUser } from '../../../store/User';
import { getDate, getValuesForColumn } from '../../../utils/helpers';
import { SKIP_ACCOUNT_ROLES } from '../../../store/Role';
import { Team } from '../../../store/Team';
import { InputNumberInterval } from './BetweenFilterInput';
import { GroupedGridColDef } from './gridColumnTypes';
import { AutoCompleteFilter } from './AutoComplete';
import { AccountData } from '../../../store/Accounts';
import { Project } from '../../../store/Project';
import { Tag } from '../../../store/Tags';
import { PriceBenchMark } from '../../../store/PriceBenchMark';

type GridColumnsData =
  | SupportCasesItem[]
  | WorkflowOrder[]
  | ServiceRequest[]
  | User[]
  | Team[]
  | AccountData[]
  | InventoryAsset[]
  | Project[]
  | EntityVoiceDetailsRecord[]
  | Tag[]
  | PriceBenchMark[]
  | GridValidRowModel[];

// Fills each column's singleSelect valueOptions from the row data (mutates the passed col
// objects, like the grid pages rely on). Callers that must not touch shared defs pass copies.
export const populateColumnValueOptions = (
  data: GridColumnsData,
  cols: GroupedGridColDef[]
): GridSingleSelectColDef[] => {
  cols.forEach((col) => {
    const filterValues = getValuesForColumn(data, col.field, col.valueOptionsComparator);

    if (Array.isArray(filterValues)) col.valueOptions = filterValues;
    if (col.field === 'tags')
      col.valueOptions = (col.valueOptions as ValueOptions[])
        .flatMap((tag) => tag)
        .reduce((acc, curr) => {
          const name = typeof curr === 'string' ? curr : curr?.name;
          if (name && !acc.includes(name)) {
            acc.push(name);
          }
          return acc;
        }, [] as string[]);
  });

  return cols;
};

export const getGridColumns = (
  data: GridColumnsData,
  defaultCols: GridSingleSelectColDef[],
  grid: string,
  // Account-hidden columns, filtered out on every render so an auto-saved personal view can't re-show
  // them. Default [] leaves callers that don't enforce an account config unaffected.
  accountDisabledFields: string[] = []
): GridSingleSelectColDef[] => {
  const currentSettings = getCurrentPreferences();
  const user = getUser();
  const gridPrefs = currentSettings.content[grid as PrefKey] as GridPreferences;
  const hasRole = user?.roles?.some((role) => SKIP_ACCOUNT_ROLES.includes(role.name));

  return populateColumnValueOptions(data, defaultCols).filter((col) => {
    const idRoleCheck = (hasRole && col.field === 'id') || col.field !== 'id';
    const sfIdRoleCheck = (hasRole && col.field === 'sfId') || col.field !== 'sfId';

    return (
      !gridPrefs?.disabledFields?.includes(col?.field) &&
      !accountDisabledFields.includes(col?.field) &&
      idRoleCheck &&
      sfIdRoleCheck
    );
  });
};

const applyTagFilterFn = (filterItem: GridFilterItem) => {
  switch (filterItem.operator) {
    case 'isAnyOf':
      if (filterItem.value == null || filterItem.value === '') {
        return null;
      }
      return (value: string | string[]) => {
        if (Array.isArray(value)) {
          return value.some((v) => filterItem.value.includes(v));
        }
        return filterItem.value.includes(value);
      };
    case 'isEmpty':
      return (value: string | string[]) => {
        if (Array.isArray(value)) {
          return value.length === 0;
        }
        return value === '';
      };
    default:
      return (value: string | string[]) => {
        if (Array.isArray(value)) {
          return value.includes(filterItem.value);
        }
        return value === filterItem.value;
      };
  }
};

const betweenOperator: GridFilterOperator = {
  label: 'between',
  value: 'between',
  getApplyFilterFn: (filterItem: GridFilterItem) => {
    if (!Array.isArray(filterItem.value) || filterItem.value.length !== 2) {
      return null;
    }
    if (!filterItem.value[0] || !filterItem.value[1]) {
      return null;
    }
    return (value): boolean => {
      const timeValue = getDate(value).getTime();
      return value != null && filterItem.value[0] <= timeValue && timeValue <= filterItem.value[1];
    };
  },
  InputComponent: InputNumberInterval
};

export const standardOperators = (): GridFilterOperator[] => {
  const isEmpty = getGridStringOperators().find((op) => op.value === 'isEmpty') as GridFilterOperator;
  const allOperators = [...getGridSingleSelectOperators(), isEmpty];

  allOperators.forEach((o) => {
    if (o?.value && !['between', 'isEmpty', 'isAnyOf'].includes(o?.value)) {
      o.InputComponent = AutoCompleteFilter;
    }
  });

  return allOperators;
};

export const booleanOperators = (): GridFilterOperator[] => {
  return getGridBooleanOperators();
};

export const allOperators = (): GridFilterOperator[] => {
  return [...standardOperators(), betweenOperator];
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const tagsOperators = (): GridFilterOperator[] => {
  return standardOperators()
    .filter((op) => op.value === 'isAnyOf' || op.value === 'isEmpty')
    .map((op) => ({
      value: op.value,
      label: op.label,
      getApplyFilterFn: (value: GridFilterItem) => applyTagFilterFn(value),
      InputComponent: op.value === 'isAnyOf' ? AutoCompleteFilter : undefined
    }));
};
